import { Tag, Button } from '@bitcine/cinesend-theme/dist'
import { convertToDateTime } from '../../helpers/convert_date'
import { useState } from 'react'

const ImportReport = ({ importData }) => {
  const { report } = importData
  const allAssets = [...(report.data.assets || []), ...(report.data.children || [])]
  const assetsCreated = allAssets.filter((asset) => asset._operation === 'create')
  const assetsUpdated = allAssets.filter((asset) => asset._operation === 'update')
  const fieldValuesCount = report.data.field_values?.length || 0
  const categoriesCount = report.data.categories?.length || 0

  const [showAllCreated, setShowAllCreated] = useState(false)
  const [showAllUpdated, setShowAllUpdated] = useState(false)

  const getAssetFieldValues = (assetUuid) => {
    return report.data.field_values?.filter((fv) => fv.asset_uuid === assetUuid).length || 0
  }

  const getAssetCategories = (assetUuid) => {
    return report.data.categories?.filter((cat) => cat.asset_uuid === assetUuid).length || 0
  }

  const renderAssetList = (assets, showAll, setShowAll, bgColor) => {
    const displayAssets = showAll ? assets : assets.slice(0, 3)

    return (
      <div className={`${bgColor} rounded-lg p-3`}>
        <div className='space-y-1'>
          {displayAssets.map((asset) => {
            const fieldValuesForAsset = getAssetFieldValues(asset.uuid)
            const categoriesForAsset = getAssetCategories(asset.uuid)

            return (
              <div key={asset.uuid} className={`text-xs space-y-1`}>
                <div>
                  • {asset.title} (Line {asset._line}){' '}
                  {asset.parent_asset_id
                    ? `(Parent: ${report.data.assets.find((a) => a.uuid === asset.parent_asset_id)?.title || 'Unknown Parent'})`
                    : ''}
                </div>
                <div className='ml-4 text-2xs text-gray-600'>
                  {fieldValuesForAsset > 0 && `${fieldValuesForAsset} field values`}
                  {fieldValuesForAsset > 0 && categoriesForAsset > 0 && ', '}
                  {categoriesForAsset > 0 && `${categoriesForAsset} categories`}
                </div>
              </div>
            )
          })}

          {!showAll && assets.length > 3 && (
            <Button tertiary size='small' onClick={() => setShowAll(true)} className='mt-2 text-xs'>
              ...and {assets.length - 3} more
            </Button>
          )}

          {showAll && assets.length > 3 && (
            <Button tertiary size='small' onClick={() => setShowAll(false)} className='mt-2 text-xs'>
              Show less
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className='space-y-4'>
        <div className='border-b pb-2'>
          <div className='font-semibold text-lg'>Import Summary</div>
          <div className='text-sm text-gray-600'>
            <b>{importData.file_name}</b> at <b>{convertToDateTime(importData.updated_at)}</b> by{' '}
            {importData.uploaded_by}
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='font-medium text-gray-700'>Assets Created:</div>
          <div className='flex items-center space-x-2'>
            <Tag outline type='success' label={`${assetsCreated.length} assets`} />
          </div>
        </div>

        {assetsCreated.length > 0 && (
          <div className='mb-4'>
            <div className='text-sm font-medium mb-2'>Created Assets:</div>
            {renderAssetList(assetsCreated, showAllCreated, setShowAllCreated, 'bg-green-100')}
          </div>
        )}

        <div className='grid grid-cols-2 gap-4'>
          <div className='font-medium text-gray-700'>Assets Updated:</div>
          <div className='flex items-center space-x-2'>
            <Tag outline type='warning' label={`${assetsUpdated.length} assets`} />
          </div>
        </div>

        {assetsUpdated.length > 0 && (
          <div className='mb-4'>
            <div className='text-sm font-medium mb-2'>Updated Assets:</div>
            {renderAssetList(assetsUpdated, showAllUpdated, setShowAllUpdated, 'bg-yellow-50')}
          </div>
        )}

        <div className='grid grid-cols-2 gap-4 pt-2 border-t'>
          <div className='space-y-2'>
            <div className='text-gray-600'>Total Field Values:</div>
            <Tag outline type='info' label={`${fieldValuesCount} field values`} />
          </div>
          <div className='space-y-2'>
            <div className='text-gray-600'>Total Categories:</div>
            <Tag outline type='info' label={`${categoriesCount} categories`} />
          </div>
        </div>
      </div>
    </>
  )
}

export default ImportReport
